<template>
  <canvas id="canvas-animation" ref="canvas"/>
</template>

<script>
function Particle(canvasObject, canvasContext, colors) {
  const canvas = canvasObject;
  const context = canvasContext;

  // Set up initial particle values
  this.radius = 3;
  this.x = Math.floor((Math.random() * canvas.width) + this.radius);
  this.y = Math.floor((Math.random() * canvas.height) + this.radius);
  this.color = colors[Math.floor(Math.random() * colors.length)];
  this.speedX = 1 - Math.random() * 2;
  this.speedY = 1 - Math.random() * 2;

  const isOutOfXBounds = () => this.x <= this.radius || this.x >= canvas.width - this.radius;
  const isOutOfYBounds = () => this.y <= this.radius || this.y >= canvas.height - this.radius;
  this.isOutOfBounds = () => isOutOfXBounds() || isOutOfYBounds();

  // This gets called on each frame
  this.update = () => {
    // Draw the particle
    context.beginPath();
    context.fillStyle = this.color;
    context.globalAlpha = 1;
    context.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    context.fill();
    context.closePath();

    // Move the particle
    this.x += this.speedX;
    this.y += this.speedY;

    // Turn it around if it's going out of bounds
    if (isOutOfXBounds()) {
      this.speedX *= -1;
    }
    if (isOutOfYBounds()) {
      this.speedY *= -1;
    }
  };
}

export default {
  props: {
    canvasVhRatio: {
      default: 0.54,
      type: Number
    },
    colors: {
      default: () => ['#f8c343', '#f7d27c', '#dada36', '#eea', '#fbee62', '#eeee44'],
      type: Array
    },
    maxCanvasWidth: {
      default: 1920,
      type: Number
    },
    maxConnectorLength: {
      default: 200.0,
      type: Number
    },
    maxParticleCount: {
      default: 100,
      type: Number
    },
    pixelsPerParticle: {
      default: 10000,
      type: Number
    }
  },
  data() {
    return {
      context: null,
      particles: [],
      particleCount: 0
    };
  },
  mounted() {
    this.context = this.$refs.canvas.getContext('2d');
    this.initializeAnimation();
    window.addEventListener('resize', this.scaleCanvas);
    window.requestAnimationFrame(this.animateFrame);
  },
  methods: {
    scaleParticleCount(canvasWidth) {
      const totalPixels = canvasWidth * window.innerHeight * this.canvasVhRatio;
      this.particleCount = Math.min(
        this.maxParticleCount,
        Math.round(totalPixels / this.pixelsPerParticle)
      );
    },
    scaleCanvas() {
      const { clientWidth } = document.body;
      const canvasWidth = clientWidth >= this.maxCanvasWidth ? this.maxCanvasWidth : clientWidth;
      this.scaleParticleCount(canvasWidth);
      this.$refs.canvas.width = canvasWidth;
      this.$refs.canvas.height = document.getElementById('canvas-overlay').clientHeight;

      // If any particles are now out of bounds, reinitialise them to randomise their position.
      // We don't just reinitialise all of them so it looks smoother.
      for (let i = 0; i < this.particleCount; i += 1) {
        // Null check is for the first run in initializeAnimation before particles are created.
        if (this.particles[i] !== undefined && this.particles[i].isOutOfBounds()) {
          this.particles[i] = new Particle(this.$refs.canvas, this.context, this.colors);
        }
      }
    },
    initializeAnimation() {
      this.scaleCanvas();

      for (let i = 0; i < this.maxParticleCount; i += 1) {
        this.particles.push(new Particle(this.$refs.canvas, this.context, this.colors));
      }
    },
    animateFrame() {
      this.context.clearRect(0, 0, this.$refs.canvas.width, this.$refs.canvas.height);

      for (let i = 0; i < this.particleCount; i += 1) {
        const currentParticle = this.particles[i];
        currentParticle.update();

        // If any other particles are close enough, draw a line between them.
        // We only consider particles after this one to avoid drawing two lines.
        for (let j = i; j < this.particleCount; j += 1) {
          const particleN = this.particles[j];
          const distanceX = particleN.x - currentParticle.x;
          const distanceY = particleN.y - currentParticle.y;
          const distance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);
          if (distance < this.maxConnectorLength) {
            this.context.beginPath();
            this.context.globalAlpha = (this.maxConnectorLength - distance)
             / this.maxConnectorLength;
            this.context.moveTo(currentParticle.x, currentParticle.y);
            this.context.lineTo(particleN.x, particleN.y);
            this.context.strokeStyle = currentParticle.color;
            this.context.stroke();
            this.context.closePath();
          }
        }
      }

      window.requestAnimationFrame(this.animateFrame);
    }
  }
};
</script>

<style lang="scss">
  #canvas-animation {
    background: radial-gradient(ellipse at bottom, $header-glow, $qv-purple-dark);
    display: block;
    height: 54vh;
    position: relative;
    width: 100%;
  }
</style>
